import React from 'react';
import Typography from 'new/elements/Typography';
import { Stack } from 'new/elements/Stack';
import DateUtils from 'js/lib/DateUtils';
import { Tooltip, CircularProgress } from '@mui/material';
import cx from 'classnames';
import * as credentialStatus from 'js/constants/credentialStatus';
import Menu from 'js/components/elements/menu/Menu';
import { useVendors } from 'new/state/vendors/repository/useVendors';
import usePaging from 'new/state/paging/repository/usePaging';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import { useCredentialProbeStatus } from 'new/hooks/useCredentialProbeStatus';
import classes from 'new/elements/flows/EnterCredential/EnterCredential.module.scss';

interface CredentialCardProps {
  credential: any;
  onCredentialSelected?: (credential: any, skipAutoRedirection?: true) => void;
  selected?: boolean;
  onClickEdit?: () => void;
  isSelectionEmpty: boolean;
}

export const CredentialCard = ({
  credential,
  onCredentialSelected,
  selected,
  onClickEdit,
  isSelectionEmpty = true,
}: CredentialCardProps) => {
  const { vendors } = useVendors();
  const { getMetaInfoById } = usePaging();
  const isProbing = useCredentialProbeStatus(credential.id);
  const vendor_id = credential?.vendor_id;
  const vendor = vendors[vendor_id];

  const meta = (() => {
    const defaultMeta = getMetaInfoById(credential?.connector?.connection_type);
    if (vendor) {
      return getMetaInfoById(vendor.name);
    }
    if (credential?.connector?.type) {
      const typeMeta = getMetaInfoById(credential.connector.type);
      if (typeMeta) return typeMeta;
      return defaultMeta;
    }
    return defaultMeta;
  })();

  const isInvalid = credential.validStatus === credentialStatus.CREDENTIAL_INVALID;

  const onOpenMenu = () => {
    if (typeof onCredentialSelected === 'function') {
      onCredentialSelected(
        credential,
        true, // skip auto redirection
      );
    }
  };

  return (
    <div
      title={meta?.displayName}
      data-testid={`credential-card-${credential.id}`}
      onClick={() => onCredentialSelected?.(credential)}
      className={cx(classes.rootCredentialContainer, {
        [classes.rootCredentialContainer_active]: selected,
        [classes.rootCredentialContainer_selectionEmpty]: isSelectionEmpty,
      })}
    >
      <div aria-label={credential.name} className={classes.credential}>
        <div className={classes.top}>
          <Stack direction="row" alignItems="center" gap="8px" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap="8px">
              <ConnectionTypeChip
                icon={meta?.iconSm}
                iconOnly
                style={{
                  padding: 'var(--spacing-sm)',
                  borderColor: 'var(--color-outline)',
                }}
              />
            </Stack>
            {onClickEdit ? (
              <span onClick={(e) => e.stopPropagation()}>
                <Menu onOpen={onOpenMenu} items={[{ label: 'Edit', onClick: onClickEdit }]} />
              </span>
            ) : null}
          </Stack>
          <Tooltip placement="top" arrow title={credential.name}>
            <Typography variant="body3">{credential.name}</Typography>
          </Tooltip>
        </div>
        <div className={classes.bottom}>
          <Typography>ID: {credential.id}</Typography>
          <Typography>Created on: {DateUtils.getDateString(credential.created_at)}</Typography>
        </div>
      </div>
      {isInvalid && !isProbing ? <span className={classes.badge_invalid}>Invalid</span> : null}
      {isProbing ? (
        <span className={classes.badge_verifying}>
          <CircularProgress size={16} />
          Connecting...
        </span>
      ) : null}
    </div>
  );
};
